import React, { useState, useEffect } from 'react';
import { streamingService } from '../services/entertainment/streaming';
import { 
  Play, 
  Pause,
  SkipForward,
  SkipBack,
  Music,
  Film,
  Volume2
} from 'lucide-react';

interface MediaItem {
  id: string;
  title: string;
  type: 'movie' | 'series' | 'music';
  platform: string;
  genre: string[];
  mood?: string[];
  rating?: number;
}

// Componente de Card reutilizável
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-[#111]/80 backdrop-blur-xl border border-white/[0.08] rounded-[20px] shadow-2xl ${className}`}>
    {children}
  </div>
);

const Entertainment = () => {
  const [currentMood, setCurrentMood] = useState('feliz');
  const [currentActivity, setCurrentActivity] = useState('relaxar');
  const [recommendations, setRecommendations] = useState<MediaItem[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);

  const moods = ['feliz', 'focado', 'energético', 'relaxado', 'romântico'];
  const activities = ['relaxar', 'trabalhar', 'exercitar', 'estudar', 'meditar'];

  useEffect(() => {
    loadRecommendations();
  }, [currentMood, currentActivity]);

  const loadRecommendations = async () => {
    const recs = await streamingService.getRecommendations(currentMood, currentActivity);
    setRecommendations(recs);
  };

  const handlePlayback = async (command: string, platform: string) => {
    await streamingService.controlPlayback(command, platform);
    if (command === 'play') setIsPlaying(true);
    if (command === 'pause') setIsPlaying(false);
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-[1400px] mx-auto py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-[22px] font-everett text-white mb-2">Entretenimento</h1>
            <p className="text-[14px] text-[#8E95A3] font-everett">
              Controle suas playlists e receba recomendações personalizadas
            </p>
          </div>
        </div>

        {/* Controles de Humor e Atividade */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6">
            <h3 className="text-[18px] font-everett text-white mb-4">Seu Humor</h3>
            <div className="flex flex-wrap gap-2">
              {moods.map((mood) => (
                <button
                  key={mood}
                  onClick={() => setCurrentMood(mood)}
                  className={`px-4 py-2 rounded-xl text-[14px] font-everett transition-all ${
                    currentMood === mood
                      ? 'bg-white text-black'
                      : 'bg-white/[0.08] text-white hover:bg-white/[0.12]'
                  }`}
                >
                  {mood}
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-[18px] font-everett text-white mb-4">Sua Atividade</h3>
            <div className="flex flex-wrap gap-2">
              {activities.map((activity) => (
                <button
                  key={activity}
                  onClick={() => setCurrentActivity(activity)}
                  className={`px-4 py-2 rounded-xl text-[14px] font-everett transition-all ${
                    currentActivity === activity
                      ? 'bg-white text-black'
                      : 'bg-white/[0.08] text-white hover:bg-white/[0.12]'
                  }`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Recomendações */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.map((item) => (
            <Card key={item.id} className="p-6 group">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-[18px] font-everett text-white mb-2">{item.title}</h3>
                  <div className="flex items-center gap-2 text-[#8E95A3]">
                    {item.type === 'music' ? (
                      <Music className="w-4 h-4" />
                    ) : (
                      <Film className="w-4 h-4" />
                    )}
                    <span className="text-[14px] font-everett">{item.platform}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePlayback(isPlaying ? 'pause' : 'play', item.platform)}
                    className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4 text-white" />
                    ) : (
                      <Play className="w-4 h-4 text-white" />
                    )}
                  </button>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-4">
                {item.genre.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-[12px] font-everett bg-white/[0.08] text-[#8E95A3]"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Controles de Reprodução */}
              <div className="mt-6 pt-4 border-t border-white/[0.08]">
                <div className="flex items-center justify-between">
                  <button className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors">
                    <SkipBack className="w-4 h-4 text-white" />
                  </button>
                  <div className="flex-1 mx-4">
                    <div className="h-1 bg-white/[0.08] rounded-full">
                      <div className="h-full w-1/3 bg-gradient-to-r from-[#1AD1A5] to-[#4C6EF5] rounded-full"></div>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors">
                    <SkipForward className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[12px] font-everett text-[#8E95A3]">1:23</span>
                  <Volume2 className="w-4 h-4 text-[#8E95A3]" />
                  <span className="text-[12px] font-everett text-[#8E95A3]">3:45</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Entertainment;
